/** Regenerate summary icon button — spinner when pending, tooltip for disabled states */

import { useEffect, useState } from 'react'
import { RefreshCw } from 'lucide-react'
import { cn } from '../../lib/utils'

interface RegenerateSummaryButtonProps {
  /** File extraction status — button disabled if not 'completed' */
  status: string | null
  /** Epoch ms of last summary regen (for cooldown computation), or null */
  lastSummaryAt: number | null
  /** True while regenerate mutation is in-flight */
  pending: boolean
  onClick: () => void
}

function getTooltip(status: string | null, cooldownSeconds: number, pending: boolean): string {
  if (pending) return 'Regenerating…'
  if (status !== 'completed') return 'Wait until extraction completes'
  if (cooldownSeconds > 0) return `Try again in ${cooldownSeconds}s`
  return 'Regenerate summary'
}

/** Compute remaining cooldown seconds given an epoch-ms timestamp */
export function computeCooldownSeconds(lastSummaryAt: number | null, cooldownMs = 60_000): number {
  if (!lastSummaryAt) return 0
  const elapsed = Date.now() - lastSummaryAt
  return Math.max(0, Math.ceil((cooldownMs - elapsed) / 1000))
}

export function RegenerateSummaryButton({
  status,
  lastSummaryAt,
  pending,
  onClick,
}: RegenerateSummaryButtonProps) {
  // Tick down once per second while cooldown is active so the tooltip updates
  // without waiting for an external refetch.
  const [cooldownSeconds, setCooldownSeconds] = useState(() => computeCooldownSeconds(lastSummaryAt))

  useEffect(() => {
    setCooldownSeconds(computeCooldownSeconds(lastSummaryAt))
    if (!lastSummaryAt) return
    const id = setInterval(() => {
      const remaining = computeCooldownSeconds(lastSummaryAt)
      setCooldownSeconds(remaining)
      if (remaining === 0) clearInterval(id)
    }, 1000)
    return () => clearInterval(id)
  }, [lastSummaryAt])

  const isDisabled = pending || status !== 'completed' || cooldownSeconds > 0
  const tooltip = getTooltip(status, cooldownSeconds, pending)

  return (
    <button
      onClick={(e) => { e.stopPropagation(); onClick() }}
      disabled={isDisabled}
      title={tooltip}
      aria-label={tooltip}
      className={cn(
        'rounded p-1 transition-colors',
        isDisabled
          ? 'cursor-not-allowed text-neutral-500'
          : 'text-neutral-400 hover:bg-white/10 hover:text-neutral-200',
      )}
    >
      <RefreshCw className={cn('h-3 w-3', pending && 'animate-spin')} />
    </button>
  )
}
